import { RRule, Frequency, Options, Weekday } from 'rrule';
import dayjs from 'dayjs';

interface GenerateRRuleOptions {
  isCustom: boolean;
  simpleSelection: string;
  interval: number;
  unit: string;
  selectedDays: string[];
  selectedMonthOption: 'day1' | 'currentDay';
}

// RRule generation
// This function generates the RRule string based on the selected recurrence options.
// step 1 : if in the occurances is selcted other than does not reapeat we neeed a rrule string
// step 2 : pass the selected options to the RRule constructor
// step 3 : create rrule for each options other than does not repeat

export const generateRRule = ({
  isCustom,
  simpleSelection,
  interval,
  unit,
  selectedDays,
  selectedMonthOption,
}: GenerateRRuleOptions): string => {
  const formattedCurrentDay = new Date().toLocaleString('en-US', { weekday: 'long' });
  const currentDayCapital =
    formattedCurrentDay.charAt(0).toUpperCase() + formattedCurrentDay.slice(1).toLowerCase();

  const dayMap = {
    Monday: RRule.MO,
    Tuesday: RRule.TU,
    Wednesday: RRule.WE,
    Thursday: RRule.TH,
    Friday: RRule.FR,
    Saturday: RRule.SA,
    Sunday: RRule.SU,
  };

  if (!isCustom) {
    switch (simpleSelection) {
      case 'none':
        return '';
      case 'daily':
        return new RRule({ freq: RRule.DAILY, interval: 1 }).toString();
      case 'weekly':
        return new RRule({
          freq: RRule.WEEKLY,
          interval: 1,
          byweekday: [dayMap[currentDayCapital as keyof typeof dayMap]],
        }).toString();
      case 'monthly':
        return new RRule({
          freq: RRule.MONTHLY,
          interval: 1,
          bymonthday: [1],
        }).toString();
      case 'annual':
        return new RRule({
          freq: RRule.YEARLY,
          interval: 1,
          bymonth: [dayjs().month() + 1],
          bymonthday: [1],
        }).toString();
      case 'weekday':
        return new RRule({
          freq: RRule.WEEKLY,
          interval: 1,
          byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
        }).toString();
      default:
        return '';
    }
  }

  const ruleOptions: Partial<Options> = {
    freq: RRule[unit.replace(/s$/, '').toUpperCase() as keyof typeof RRule] as Frequency,
    interval,
  };

  if (unit === 'week' || unit === 'weeks') {
    if (selectedDays.length > 0) {
      ruleOptions.byweekday = selectedDays.map(day => {
        const weekdayKey = day.substring(0, 2).toUpperCase() as keyof typeof RRule;
        const weekday = RRule[weekdayKey];
        if (weekday instanceof Weekday) {
          return weekday;
        }
        throw new Error(`Invalid weekday: ${day}`);
      });
    }
  }

  if (unit === 'month' || unit === 'months') {
    if (selectedMonthOption === 'day1') {
      ruleOptions.bymonthday = [1];
    } else {
      const currentDay = dayjs();
      const currentDayOfWeek = currentDay.day(); // Sunday = 0, Monday = 1, etc.
      const currentWeekOfMonth = Math.ceil(currentDay.date() / 7);
      ruleOptions.byweekday = [currentDayOfWeek];
      ruleOptions.bysetpos = [currentWeekOfMonth];
    }
  }

  if (unit === 'year' || unit === 'years') {
    ruleOptions.bymonth = [dayjs().month() + 1];
    if (selectedMonthOption === 'day1') {
      ruleOptions.bymonthday = [1];
    } else {
      const currentDay = dayjs();
      const currentDayOfWeek = currentDay.day();
      const currentWeekOfMonth = Math.ceil(currentDay.date() / 7);
      ruleOptions.byweekday = [currentDayOfWeek];
      ruleOptions.bysetpos = [currentWeekOfMonth];
    }
  }

  return new RRule(ruleOptions).toString();
};

export const formattedCurrentDay = () => {
  const currentDay = new Date().toLocaleString('en-US', { weekday: 'long' });
  return currentDay.charAt(0).toUpperCase() + currentDay.slice(1).toLowerCase();
};

export const formattedCurrentMonth = () => {
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  return currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1).toLowerCase();
};

export const getWeekday = (): Weekday => {
  const day = formattedCurrentDay().slice(0, 2).toUpperCase(); // "MO", "TU", etc.
  return RRule[day as keyof typeof RRule] as Weekday;
};

export const recurrenceOptions = (): any => [
  { value: 'none', label: 'Does not repeat' },
  // { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: `Weekly on ${formattedCurrentDay()}` },
  { value: 'monthly', label: `Monthly on First ${formattedCurrentDay()}` },
  { value: 'annual', label: `Annually on ${formattedCurrentMonth()} 1` },
  { value: 'weekday', label: 'Every WeekDay (Monday to Friday)' },
  { value: 'custom', label: 'Custom' },
];

export const today = () => dayjs().format('YYYY-MM-DD');
export const maxDate = () => dayjs().add(12, 'month').format('YYYY-MM-DD');
export const dayMap: { [key: string]: Weekday } = {
  SUNDAY: RRule.SU,
  MONDAY: RRule.MO,
  TUESDAY: RRule.TU,
  WEDNESDAY: RRule.WE,
  THURSDAY: RRule.TH,
  FRIDAY: RRule.FR,
  SATURDAY: RRule.SA,
};
export const formatUnitDisplay = (unit: string, interval: number): string => {
  const baseUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
  return interval > 1 ? `${baseUnit}s` : baseUnit;
};

// export const recurrenceUnits = ['day', 'week', 'month', 'year'];
export const recurrenceUnits = ['week', 'month', 'year'];
export const weekDays = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];
